import imageCompression from 'browser-image-compression';

/**
 * Format file size for logging
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Compress an image file
 * @param {File} file - Original image file
 * @returns {Promise<File>} Compressed image file
 */
export async function compressImage(file) {
  try {
    console.log('🗜️ Starting image compression:', {
      name: file.name,
      size: formatFileSize(file.size),
      type: file.type
    });

    // Skip compression for already small files (< 500KB)
    if (file.size < 500 * 1024) {
      console.log('⏭️ Image already small, skipping compression');
      return file;
    }

    // Skip compression for GIFs (would lose animation) and SVGs (vector graphics)
    if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
      console.log('⏭️ Skipping compression for GIF/SVG');
      return file;
    }

    // Compression options
    const options = {
      maxSizeMB: 1,              // Target max 1MB
      maxWidthOrHeight: 1920,    // Max dimension for Full HD displays
      useWebWorker: true,        // Non-blocking compression
      initialQuality: 0.8,       // 80% quality (visually lossless)
      // Keep original format for PNG/WebP, convert photos to JPEG
      fileType: file.type === 'image/png' || file.type === 'image/webp'
        ? file.type
        : 'image/jpeg'
    };

    const compressedFile = await imageCompression(file, options);

    // Only use compressed version if it's actually smaller
    if (compressedFile.size < file.size) {
      const reduction = Math.round((1 - compressedFile.size / file.size) * 100);
      console.log('✅ Image compressed:', {
        originalSize: formatFileSize(file.size),
        compressedSize: formatFileSize(compressedFile.size),
        reduction: `${reduction}%`
      });
      return compressedFile;
    } else {
      console.log('⚠️ Compressed file larger than original, using original');
      return file;
    }
  } catch (error) {
    console.warn('⚠️ Image compression failed, using original:', error);
    return file;
  }
}

/**
 * Compress a video file using MediaRecorder API
 * @param {File} file - Original video file
 * @param {Function} onProgress - Optional progress callback (0-1)
 * @returns {Promise<File>} Re-encoded video file
 */
export async function compressVideo(file, onProgress = null) {
  try {
    console.log('🗜️ Starting video compression:', {
      name: file.name,
      size: formatFileSize(file.size),
      type: file.type
    });

    // Skip compression for already small videos (< 10MB)
    if (file.size < 10 * 1024 * 1024) {
      console.log('⏭️ Video already small, skipping compression');
      return file;
    }

    // Check MediaRecorder support
    if (!window.MediaRecorder || !window.MediaRecorder.isTypeSupported) {
      console.warn('⚠️ MediaRecorder not supported, uploading original');
      return file;
    }

    // Determine best supported mime type
    let mimeType = 'video/webm;codecs=vp8,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/mp4';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          console.warn('⚠️ No supported video mime types, uploading original');
          return file;
        }
      }
    }

    console.log('🎬 Using mime type:', mimeType);

    // Create video element to read the file
    const videoElement = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    videoElement.src = videoUrl;
    videoElement.muted = true;

    // Wait for video metadata to load
    await new Promise((resolve, reject) => {
      videoElement.onloadedmetadata = resolve;
      videoElement.onerror = () => reject(new Error('Failed to load video'));
      // Timeout after 10 seconds
      setTimeout(() => reject(new Error('Video loading timeout')), 10000);
    });

    const duration = videoElement.duration;
    console.log('📹 Video duration:', duration.toFixed(2), 'seconds');

    // Create canvas to capture video frames
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext('2d');

    // Capture video stream from canvas
    const canvasStream = canvas.captureStream(30); // 30 FPS

    // Get audio track from original video if available
    const videoStream = videoElement.captureStream ? videoElement.captureStream() : null;
    const audioTrack = videoStream?.getAudioTracks()[0];
    if (audioTrack) {
      canvasStream.addTrack(audioTrack);
    }

    // Set up MediaRecorder with lower bitrate
    const mediaRecorder = new MediaRecorder(canvasStream, {
      mimeType: mimeType,
      videoBitsPerSecond: 2500000  // 2.5 Mbps (good quality, much smaller)
    });

    const chunks = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    // Start recording and playing video
    const recordingPromise = new Promise((resolve, reject) => {
      mediaRecorder.onstop = () => {
        URL.revokeObjectURL(videoUrl);
        resolve();
      };
      mediaRecorder.onerror = (error) => {
        URL.revokeObjectURL(videoUrl);
        reject(error);
      };
    });

    mediaRecorder.start(100); // Collect data every 100ms
    videoElement.play();

    // Update progress based on video playback
    const progressInterval = setInterval(() => {
      if (onProgress && duration > 0) {
        const progress = Math.min(videoElement.currentTime / duration, 1);
        onProgress(progress);
      }
    }, 200);

    // Wait for video to finish
    await new Promise((resolve) => {
      videoElement.onended = resolve;
    });

    // Stop recording
    mediaRecorder.stop();
    await recordingPromise;
    clearInterval(progressInterval);

    // Stop all tracks
    canvasStream.getTracks().forEach(track => track.stop());
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
    }

    // Create compressed file blob
    const compressedBlob = new Blob(chunks, { type: mimeType });

    // Only use compressed version if it's actually smaller and non-empty
    if (compressedBlob.size > 0 && compressedBlob.size < file.size) {
      const reduction = Math.round((1 - compressedBlob.size / file.size) * 100);
      console.log('✅ Video compressed:', {
        originalSize: formatFileSize(file.size),
        compressedSize: formatFileSize(compressedBlob.size),
        reduction: `${reduction}%`
      });

      // Determine file extension based on mime type
      let extension = '.webm';
      if (mimeType.includes('mp4')) {
        extension = '.mp4';
      }

      // Create new File object with appropriate name
      const originalName = file.name.replace(/\.[^/.]+$/, '');
      const compressedFile = new File(
        [compressedBlob],
        `${originalName}${extension}`,
        { type: mimeType }
      );

      if (onProgress) {
        onProgress(1); // 100% complete
      }

      return compressedFile;
    } else {
      console.log('⚠️ Compressed video larger than original, using original');
      return file;
    }
  } catch (error) {
    console.warn('⚠️ Video compression failed, using original:', error);
    return file;
  }
}

/**
 * Main entry point - detects type and compresses accordingly
 * @param {File} file - Original file
 * @param {Function} onProgress - Optional progress callback for video compression (0-1)
 * @returns {Promise<File>} Compressed file (or original if compression fails/not applicable)
 */
export async function compressFile(file, onProgress = null) {
  if (!file) {
    return file;
  }

  const fileType = file.type.toLowerCase();

  // Compress images
  if (fileType.startsWith('image/')) {
    return await compressImage(file);
  }

  // Skip video compression — MediaRecorder approach is real-time only and unreliable
  // for large files; chunked signed-URL upload handles videos natively
  if (fileType.startsWith('video/')) {
    console.log('⏭️ Skipping video compression, using chunked upload');
    return file;
  }

  // For other file types (audio, documents), return as-is
  console.log('⏭️ File type not supported for compression:', fileType);
  return file;
}
